import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { EnvConfig, Environment, CacheService, ResumableDownloadConfig } from '@doc/core';
import { ApiUrls } from '../document-urls';
import {
  createDiskSavedBlob,
  DownloadStreamOptions,
  FileSystemWritableFileStreamLike,
  openDiskWritable,
} from '../disk-download';
import { ApiUrls as BackofficeApiUrls } from '../backoffice-urls';
import {
  BaseResponse,
  EventDeleteRequest,
  EventGetListRequest,
  EventGetListResponse,
  EventInsertRequest,
} from '../models';
import { GlobalSearchPayload, PaginatedTable } from '@doc/utils';
import { BehaviorSubject, Observable, firstValueFrom, from, map, of, tap, throwError, timer, defer } from 'rxjs';
import { options } from '@fullcalendar/core/preact';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { catchError, filter, finalize, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { DownloadActivityService } from './download-activity.service';
import { ensureDownloadDirectory, isFsaSupported } from '../fsa-download-dir';
import { NgxSpinnerService } from 'ngx-spinner';
@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private http = inject(HttpClient);
  private readonly downloadActivity = inject(DownloadActivityService);
  private apiUrl: string = '';
  private baseUrl: string;
  private previousUrl: string | null = null;
  private previousQueryParams: any = null;
  private currentUrl: string | null = null;
  private statusSubscription: Subscription | null = null;
  private processTimer: number = 0;
  private processDocInterval: number = 0;

  private driveType: string = '';
  constructor(
    @Inject(EnvConfig) private config: Environment,
    private cacheService: CacheService,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    public router: Router
  ) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
    this.processTimer = this.config.ProcessDocApiLimit;
    this.processDocInterval = this.config.ProcessDocApiInterval;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const navigation = this.router.currentNavigation();
        if (navigation?.extras?.queryParams) {
          this.previousQueryParams = navigation.extras.queryParams;
        }
      }

      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      }
    });
  }

  getPreviousUrl(): { url: string | null; queryParams: any } {
    return { url: this.previousUrl, queryParams: this.previousQueryParams };
  }

  getFoldersandPermissions(body: any) {
    const link = this.apiUrl + ApiUrls.getFolders;
    return this.http.post<any>(link, body);
  }

  getFolders(body: any) {
    const link = this.apiUrl + ApiUrls.getFiles;
    return this.http.post<any>(link, body);
  }

  getFolderData(folderType: string, body: any) {
    const links: any = {
      'my-drive': ApiUrls.getMyFolders,
      'shared-with-me': ApiUrls.getFoldersSharedWithUser,
      'shared-by-me': ApiUrls.getFolderSharedByUser,
    };

    const link = this.apiUrl + (links[folderType] || links['my-drive']);
    return this.http.post<any>(link, body);
  }

  getFolderNames(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFoldersNames;
    return this.http.post<any>(link, body);
  }

  getFolderByCategory(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFolders;
    return this.http.post<any>(link, body);
  }

  getAllFavourites(body: any) {
    const link = this.apiUrl + ApiUrls.getAllFavourites;
    return this.http.post<any>(link, body);
  }

  saveDocToFavourite(body: any) {
    const link = this.apiUrl + ApiUrls.saveDocToFavourite;
    return this.http.post<any>(link, body);
  }

  saveDocToFolder(body: any) {
    const link = this.apiUrl + ApiUrls.saveDocToFolder;
    return this.http.post<any>(link, body);
  }

  transferFileToFolder(body: any) {
    const link = this.apiUrl + ApiUrls.transferFileToFolder;
    return this.http.post<any>(link, body);
  }

  moveFolder(body: any) {
    const link = this.apiUrl + ApiUrls.moveFolder;
    return this.http.post<any>(link, body);
  }

  duplicateFile(body: {
    fileId: number;
    targetFolderId?: number;
    targetLibraryType?: number;
    targetDepartmentId?: number | null;
  }) {
    const link = this.apiUrl + ApiUrls.duplicateFile;
    return this.http.post<any>(link, body);
  }

  duplicateFolder(body: {
    folderId: number;
    targetParentId?: number | null;
    targetLibraryType?: number;
    targetDepartmentId?: number | null;
  }) {
    const link = this.apiUrl + ApiUrls.duplicateFolder;
    return this.http.post<any>(link, body);
  }

  deleteFromFavourites(body: any) {
    const link = this.apiUrl + ApiUrls.deleteFromFavourites;
    return this.http.post<any>(link, body);
  }

  // createFolder(body: any) {
  //   const link = `${this.apiUrl}/api/Folder/create`;
  //   return this.http.post<any>(link, body);
  // }
  createFolder(body: any) {
    const link = this.apiUrl + ApiUrls.createFolder;
    return this.http.post<any>(link, body);
  }

  // updateFolder(id: number, body: any) {
  //   const link = `${this.apiUrl}/api/Folder/update/${id}`;
  //   return this.http.put<any>(link, body);
  // }
  // updateFolder(body: any) {
  //   const link = this.apiUrl + ApiUrls.updateFolder;
  //   return this.http.post<any>(link, body);
  // }

  updateFolderByCategory(id: number, body: any) {
    const link = `${this.apiUrl}/api/Folder/update/${id}`;
    return this.http.post<any>(link, body);
  }
  // deleteFolder(id: number) {
  //   const link = `${this.apiUrl}/api/Folder/delete/${id}`;
  //   return this.http.delete<any>(link);
  // }
  deleteFolder(id: any) {
    const link = this.apiUrl + ApiUrls.deleteFolder + id;
    return this.http.post<any>(link, null);
  }

  getAllFilesInFolder(body: any) {
    const link = this.apiUrl + ApiUrls.getfilesinfolder;
    return this.http.post<any>(link, body);
  }
  getOutbox(body: any) {
    const link = this.apiUrl + ApiUrls.getOutbox;
    return this.http.post<any>(link, body);
  }
  transferDocumentOwnership(body: any) {
    const link = this.apiUrl + ApiUrls.transferDocownership;
    return this.http.post<any>(link, body);
  }
  getAdminOutbox(body: any) {
    const link = this.apiUrl + ApiUrls.getAdminOutbox;
    return this.http.post<any>(link, body);
  }
  getInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getInbox;
    return this.http.post<any>(link, body);
  }
  getTasks(body: any) {
    const link = this.apiUrl + '/api/WorkFlowDetails/GetTasks';
    return this.http.post<any>(link, body);
  }

  getMeetingList(body: any) {
    const link = this.apiUrl + ApiUrls.CalenderMeetings;
    return this.http.post<any>(link, body);
  }

  getTasksByUser(body: any) {
    const link = this.apiUrl + ApiUrls.GetTasksByUser;
    return this.http.post<any>(link, body);
  }
  updateTasksByUser(body: any) {
    const link = this.apiUrl + ApiUrls.UpdateTaskByUser;
    return this.http.post<any>(link, body);
  }
  getAdminInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getAdminInbox;
    return this.http.post<any>(link, body);
  }
  askQuestion(body: any) {
    const link = this.apiUrl + '/api/DocumentProcessing/Askquestion';
    return this.http.post<any>(link, body);
  }
  getEmployeeInbox(body: any) {
    const link = this.apiUrl + ApiUrls.getEmployeeInbox;
    return this.http.post<any>(link, body);
  }

  deleteFileFromFolder(body: any) {
    const link = this.apiUrl + ApiUrls.deleteFileFromFolder;
    return this.http.post<any>(link, body);
  }

  deleteFolderByCategory(id: number) {
    const link = `${this.apiUrl}/api/Folder/delete/${id}`;
    return this.http.post<any>(link, id);
  }

  // shareFolder(body: any) {
  //   const link = `${this.apiUrl}${ApiUrls.createFolderPermission}`;
  //   return this.http.post<any>(link, body);
  // }

  getFileData(fileType: string, body: any) {
    const links: any = {
      'my-drive': ApiUrls.getMyFiles,
      'shared-with-me': ApiUrls.getFilesSharedWithUser,
      'shared-by-me': ApiUrls.getFilesSharedByUser,
    };

    const link = this.apiUrl + (links[fileType] || links['my-drive']);
    return this.http.post<any>(link, body);
  }

  getFileDataByCategory(body: any) {
    const link = this.apiUrl + ApiUrls.getMyFiles;
    return this.http.post<any>(link, body);
  }

  getFileById(id: number) {
    const link = this.apiUrl + ApiUrls.getFileById + `/${id}`;
    return this.http.get<any>(link);
  }

  resolveGoToLocation(body: { entityType: 'file' | 'folder'; entityId: number }) {
    // Temporarily disabled: /api/File/resolve-location
    // const link = this.apiUrl + ApiUrls.resolveGoToLocation;
    // return this.http.post<any>(link, body);
    return throwError(() => new Error('resolveGoToLocation API is disabled'));
  }
  getFileByPath(filePath: string) {
    const link =
      this.apiUrl + ApiUrls.getFileByPath + `/${encodeURIComponent(filePath)}`;
    return this.http.get<any>(link);
  }

  updateFile(id: number, body: any) {
    const link = `${this.apiUrl}/api/File/update/${id}`;
    return this.http.put<any>(link, body);
  }

  updateFileByCategory(id: number, body: any) {
    const link = `${this.apiUrl}/api/File/update/${id}`;
    return this.http.post<any>(link, body);
  }

  // deleteFile(id: number) {
  //   const link = `${this.apiUrl}/api/File/delete/${id}`;
  //   return this.http.delete<any>(link);
  // }

  deleteFile(id: number) {
    const link = this.apiUrl + ApiUrls.deleteFile + `${id}`;
    return this.http.post<any>(link, {});
  }

  deleteFileByCategory(id: number) {
    const link = `${this.apiUrl}/api/File/delete/${id}`;
    return this.http.post<any>(link, id);
  }

  deleteBulkMedia(payload: { FileIds: number[], FolderIds: number[] }) {
    const link = this.apiUrl + ApiUrls.multiDelete;
    return this.http.post<any>(link, payload);
  }

  // shareFile(body: any) {
  //   const link = `${this.apiUrl}${ApiUrls.createFilePermission}`;
  //   return this.http.post<any>(link, body);
  // }

  createFolderPermission(body: any) {
    const link = `${this.apiUrl}/api/Folder/createFolderPermission`;
    return this.http.post<any>(link, body);
  }

  removeFolderPermission(id: any) {
    const link = `${this.apiUrl}/api/Folder/deleteFolderPermission?permissionId=${id}`;
    return this.http.post<any>(link, { permissionId: id });
  }

  removeFilePermission(id: any) {
    const link = `${this.apiUrl}/api/File/deleteFilePermission?permissionId=${id}`;
    return this.http.post<any>(link, { permissionId: id });
  }

  getDocumentProcessingList(payload: any) {
    const link = `${this.apiUrl}/api/DocumentProcessing/getDocumentProcessingList`;
    return this.http.post<any>(link, payload);
  }

  uploadFiles(formData: FormData) {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const link = this.apiUrl + ApiUrls.upload;
    return this.http.post(link, formData, { headers });
  }

  uploadRejectedFiles(formData: FormData) {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const link = this.apiUrl + ApiUrls.reupload;
    return this.http.post(link, formData, { headers });
  }

  reuploadFile(fileId: number, approverId: number, formData: FormData) {
    const link = `${this.apiUrl}${ApiUrls.reuploadFile}/${fileId}`;
    const params = new HttpParams().set('approverId', String(approverId));
    return this.http.post<any>(link, formData, { params });
  }

  downloadFile(filePath: string): Observable<HttpEvent<Blob>> {
    const url = this.apiUrl + ApiUrls.download + `/${filePath}`;
    return this.http.get<Blob>(url, {
      observe: 'events',
      responseType: 'blob' as 'json',
    });
  }

  // downloadFile(filePath: any): Observable<{blob: Blob, type: string}> {
  //   const url = this.apiUrl + ApiUrls.download + `/${filePath}`;
  //   return this.http.get(url, {
  //     observe: 'response',
  //     responseType: 'blob'
  //   }).pipe(
  //     map((response:any) => {
  //       return {
  //         blob: response.body,
  //         type: response.headers.get('Content-Type')
  //       };
  //     })
  //   );
  // }

  generateRequest(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/GenerateRequest`;
    return this.http.post<any>(url, payload);
  }
  getAllSearchData(): Observable<any> {
    const url = `${this.apiUrl}/api/History/GetAllSearchData`;
    return this.http.get<any>(url);
  }
  getAllSearchDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/GetSearchDetails`;
    return this.http.post<any>(url, payload);
  }
  getImage(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/getImage`;
    return this.http.post<any>(url, payload);
  }

  updateGeneratedDocuments(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/uploadGenDoc`;
    return this.http.post<any>(url, payload);
  }

  SearchCustomsData(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/SearchDocument`;
    return this.http.post<any>(url, payload);
  }

  TriggerMail(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/TriggerMail`;
    return this.http.post<any>(url, payload);
  }
  GetCustomsDocument(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocument`;
    return this.http.post<any>(url, payload);
  }

  GetAllDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getAllDocByType;
    return this.http.post<any>(url, payload);
  }

  GetAllBrowseDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getAllDocuments;
    return this.http.post<any>(url, payload);
  }

  getAllSharedDocuments(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getSharedDocument;
    return this.http.post<any>(url, payload);
  }

  getSharedDocumentIds(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getSharedDocumentIds;
    return this.http.post<any>(url, payload);
  }

  getSharedUsers(docId: any) {
    const url = this.apiUrl + ApiUrls.getSharedUsers + '/' + docId;
    return this.http.get<any>(url);
  }

  shareDocument(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.shareDocument;
    return this.http.post<any>(url, payload);
  }

  getDocumentSharedTemplates(): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDocumentSharedTemplates;
    return this.http.get<any>(url);
  }

  revokeShareDocument(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.revokeShareDocument;
    return this.http.post<any>(url, payload);
  }

  GetDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocumentData`;
    return this.http.post<any>(url, payload);
  }

  ReadDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/readDataDoc`;
    return this.http.post<any>(url, payload);
  }

  OnlyReadDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/onlyReadDataDoc`;
    return this.http.post<any>(url, payload);
  }
  saveDocD(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/saveReadDataDoc`;
    return this.http.post<any>(url, payload);
  }

  ReadAllGeneratedDocuments(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/History/ReadAllGeneratedDocuments`;
    return this.http.post<any>(url, payload);
  }

  Deletedocument(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/DeleteDocument`;
    return this.http.post<any>(url, payload);
  }
  ProcessWorkflow(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/ProcessWorkFlow`;
    return this.http.post<any>(url, payload);
  }
  CreateTranslationWorkflow(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/InitiateTranslation`;
    return this.http.post<any>(url, payload);
  }
  getPendingFilesByUser(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/GetAllPendingFilesByUser`;
    return this.http.post<any>(url, payload);
  }

  fileReview(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/ApproveOrReject`;
    return this.http.post<any>(url, payload);
  }

  fileApprovalHistoryById(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/File/GetAllFilesHistoryByFileId`;
    return this.http.post<any>(url, payload);
  }
  getDocumentDetails(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetDocumentDetails/` + payload;
    return this.http.get<any>(url);
  }

  GetUploadedFile(payload: any): Observable<any> {
    const cacheKey = `uploadedFile_${payload}`;
    const cachedData = this.cacheService.getCache(cacheKey);
    if (cachedData) {
      console.log('Serving from cache');
      return of(cachedData);
    }

    const url = `${this.apiUrl}/api/Document/GetUploadedFile/` + payload;
    return this.http
      .get<any>(url, {
        responseType: 'blob' as 'json',
      })
      .pipe(tap((data) => this.cacheService.setCache(cacheKey, data)));
  }

  GetTranslatedFile(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetTransaltedFile/` + payload;
    return this.http.get<any>(url, {
      responseType: 'blob' as 'json',
    });
  }

  GetUploadedProcessedFile(payload: any): Observable<any> {
    const url =
      `${this.apiUrl}/api/DocumentProcessing/getProcessedDocumentFile/` +
      payload;
    return this.http.get<any>(url, {
      responseType: 'blob' as 'json',
    });

    responseType: 'blob' as 'json';
  }

  private updateOption = new BehaviorSubject<any>(null);
  checkUploadOption = this.updateOption.asObservable();
  passUpdateOption(option: any) {
    this.updateOption.next(option);
  }

  // getFileAccessDetails(payload: any): Observable<any> {
  //   const url = `${this.apiUrl}/api/File/GetAllFilesPermmissionByFileId`;
  //   return this.http.post<any>(url, payload)
  // }

  getFileAccessDetailsById(id: number): Observable<any> {
    const url = `${this.apiUrl}/api/File/getFilePermissionById?id=${id}`;
    return this.http.get<any>(url);
  }

  getFolderAccessDetailsById(id: number): Observable<any> {
    const url = `${this.apiUrl}/api/Folder/getFolderPermissionsById?id=${id}`;
    return this.http.get<any>(url);
  }

  GetAllFilesPermmissionByFileId(payload: any): Observable<any> {
    //GetAllFilesPermmissionByFileId'
    const url = `${this.apiUrl}/api/File/GetAllFilesPermmissionByFileId`;
    return this.http.post<any>(url, payload);
  }

  GetAllFilesPermmissionByFolderId(payload: any): Observable<any> {
    //GetAllFilesPermmissionByFileId'
    const url = `${this.apiUrl}/api/Folder/GetAllFolderPermmissionByFolderId`;
    return this.http.post<any>(url, payload);
  }

  getUserPendingTasks(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetAllPendingTasksByUser`;
    return this.http.post<any>(url, payload);
  }

  //NEW Services
  createReportAgrMou(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createReportAgrMou;
    return this.http.post<BaseResponse>(url, payload);
  }
  createReportPom(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createReportPom;
    return this.http.post<BaseResponse>(url, payload);
  }

  searchAgrMou(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.searchAgrMou;
    return this.http.post<BaseResponse>(url, payload);
  }

  searchPom(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.searchPom;
    return this.http.post<BaseResponse>(url, payload);
  }

  getFilter(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getFilter + payload.docType;
    return this.http.get<BaseResponse>(url);
  }

  getAllFilter(): Observable<any> {
    const url = `${this.apiUrl}/api/Document/GetFiltersByUser`;
    return this.http.get<any>(url);
  }

  createFilter(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.createFilter;
    return this.http.post<BaseResponse>(url, payload);
  }

  deleteFilter(id: number) {
    const link = this.apiUrl + ApiUrls.deleteFilter + `/${id}`;
    return this.http.post<any>(link, id);
  }

  // downloadReport(payload: any): Observable<any> {
  //   const url = this.apiUrl + ApiUrls.downloadReport;
  //   return this.http.post(url, payload);
  // }

  downloadReport(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadReport;
    return this.http.post(url, requestObject, { responseType: 'blob' });
  }

  exportInbox(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.exportInbox;
    return this.http.post(url, requestObject, { responseType: 'blob' });
  }

  UploadAiProcessFile(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
      'X-Skip-Loader': 'true',
    });
    const link = this.apiUrl + ApiUrls.uploadai;
    return this.http.post(link, formData, { headers });
  }

  ReadAiInfo(payload: any): Observable<any> {
    const url =
      `${this.apiUrl}/api/DocumentProcessing/getProcessedDocument/` + payload;
    return this.http.get<any>(url);
  }

  SaveDocument(payload: FormData | Record<string, unknown>): Observable<any> {
    const url = this.apiUrl + '/api/Document/SaveDocument';
    return this.http.post<any>(url, payload);
  }

  getSavedDocuments(payload: Record<string, unknown>): Observable<any> {
    const url = this.apiUrl + ApiUrls.getSavedDocuments;
    return this.http.post<any>(url, payload);
  }

  renameSavedDocument(payload: { fileId: number; subject: string }): Observable<any> {
    const url = this.apiUrl + ApiUrls.renameSavedDocument;
    return this.http.post<any>(url, payload);
  }

  getFileDetails(fileId: number): Observable<any> {
    const url = `${this.apiUrl}${ApiUrls.getFileDetails}/${fileId}`;
    return this.http.get<any>(url);
  }

  getSavedDocumentFromCloud(fileId: number, attachmentId?: number): Observable<Blob> {
    let url = `${this.apiUrl}${ApiUrls.getSavedDocumentFromCloud}/${fileId}`;
    if (attachmentId != null && attachmentId > 0) {
      url += `?attachmentId=${attachmentId}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  getSavedDocumentPreviewPdf(fileId: number, attachmentId?: number): Observable<Blob> {
    let url = `${this.apiUrl}${ApiUrls.getSavedDocumentPreviewPdf}/${fileId}`;
    if (attachmentId != null && attachmentId > 0) {
      url += `?attachmentId=${attachmentId}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  getSavedDocumentExport(fileId: number, format: 'pdf' | 'docx' = 'pdf'): Observable<Blob> {
    const url = `${this.apiUrl}${ApiUrls.getSavedDocumentExport}/${fileId}?format=${format}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getNextDocumentNumbers(departmentId?: number): Observable<any> {
    let url = `${this.apiUrl}${ApiUrls.getNextDocumentNumbers}`;
    if (departmentId != null && departmentId > 0) {
      url += `?departmentId=${departmentId}`;
    }
    return this.http.get<any>(url);
  }

  getCreateDocumentLookups(departmentId?: number): Observable<any> {
    let url = `${this.apiUrl}${ApiUrls.getCreateDocumentLookups}`;
    if (departmentId != null && departmentId > 0) {
      url += `?departmentId=${departmentId}`;
    }
    return this.http.get<any>(url);
  }

  deleteSavedDocumentAttachment(attachmentId: number): Observable<any> {
    const url = `${this.apiUrl}${ApiUrls.deleteSavedDocumentAttachment}/${attachmentId}`;
    return this.http.delete<any>(url);
  }

  uploadDocumentAttachment(fileId: number, formData: FormData): Observable<any> {
    const url = `${this.apiUrl}${ApiUrls.uploadDocumentAttachment}/${fileId}`;
    return this.http.post<any>(url, formData);
  }

  deleteSavedDocument(fileId: number): Observable<any> {
    const url = `${this.apiUrl}${ApiUrls.deleteSavedDocument}/${fileId}`;
    return this.http.delete<any>(url);
  }

  saveTodraft(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    // 'https://obliging-formerly-titmouse.ngrok-free.app' +
    const url = this.apiUrl + ApiUrls.saveToDraft;
    return this.http.post<any>(url, payload, { headers });
  }
  loadDraftValues(payload: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDraftDataById + payload;
    return this.http.get<any>(url);
  }

  retryAiProcessing(documentProcessingId: any, payload: any) {
    const url =
      this.apiUrl + ApiUrls.retryAIProcessing + '/' + documentProcessingId;
    return this.http.post<any>(url, payload);
  }
  documentSoftDelete(documentProcessingId: any) {
    const url =
      this.apiUrl + ApiUrls.documentSoftDelete + '/' + documentProcessingId;
    return this.http.post<any>(url, null);
  }

  createAndSaveDocument(body: any) {
    const url = this.apiUrl + ApiUrls.createDocument;
    return this.http.post<any>(url, body);
  }

  getSubjectByDocType(docType: any) {
    const url = this.apiUrl + ApiUrls.getSubjectByDocType + '/' + docType;
    return this.http.get<any>(url);
  }

  getAllDraftData(body: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.getDraftDocuments;
    return this.http.post<any>(url, body);
  }

  getUploadedAttachments(id: any) {
    const url = this.apiUrl + ApiUrls.getUploadedAttachments + '/' + id;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  downloadPomReport(id: any) {
    const url = this.apiUrl + ApiUrls.getPOMReportTemplate + '/' + id;
    return this.http.get(url, { responseType: 'blob' });
  }

  getUploadedPomReportAttachments(id: any) {
    const url =
      this.apiUrl + ApiUrls.getUploadedPomReportAttachments + '/' + id;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  viewUpoadedPomReportAttachments(id: any) {
    const url =
      this.apiUrl + ApiUrls.getUploadedPomReportAttachments + '/' + id;
    return this.http.get(url, { responseType: 'blob' });
  }
  viewUpoadedPomAttachments(id: any) {
    const url = this.apiUrl + ApiUrls.getUploadedAttachments + '/' + id;
    return this.http.get(url, { responseType: 'blob' });
  }
  deleteDraftDocument(body: any) {
    const url = this.apiUrl + ApiUrls.deleteDraftDocuments;
    return this.http.post<any>(url, body);
  }

  getParticipantNames(docId: any) {
    const url = this.apiUrl + ApiUrls.GetMeetingParticipants + '/' + docId;
    return this.http.get<any>(url);
  }

  getObservationByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getDocumentObservation + '/' + docId;
    return this.http.get<any>(url);
  }

  getLatestDevelopmentByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getLatestDevelopment + '/' + docId;
    return this.http.get<any>(url);
  }

  addEditObservation(payload: any) {
    const url = this.apiUrl + ApiUrls.addUpdateObservation;
    return this.http.post<any>(url, payload);
  }

  addUpdateLatestDevelopment(payload: any) {
    const url = this.apiUrl + ApiUrls.addUpdateLatestDevelopment;
    return this.http.post<any>(url, payload);
  }

  deleteObservation(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteObservation + '/' + docId;
    return this.http.post<any>(url, null);
  }

  deleteLatestDevelopment(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteLatestDevelopment + '/' + docId;
    return this.http.post<any>(url, null);
  }

  createTask(body: any) {
    const url = this.apiUrl + ApiUrls.createTasks;
    return this.http.post<any>(url, body);
  }

  getDocumentLog(documentId: any, type: any) {
    const url =
      this.apiUrl + ApiUrls.documentLogs + '/' + documentId + '/' + type;
    return this.http.get<any>(url);
  }
  getTaskLog(documentId: any, type: any) {
    const url = this.apiUrl + ApiUrls.taskLogs + '/' + documentId + '/' + type;
    return this.http.get<any>(url);
  }
  getWorkflowLog(documentId: any) {
    const url = this.apiUrl + ApiUrls.workflowLog + '/' + documentId;
    return this.http.get<any>(url);
  }

  uploadUpdateAttachment(body: any, params: any) {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const url = this.apiUrl + ApiUrls.uploadUpdateAttachment;
    return this.http.post<any>(url, body, { headers: headers, params: params });
  }

  uploadUpdatePomReport(body: any, params: any) {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const url = this.apiUrl + ApiUrls.uploadUpdatePomReport;
    return this.http.post<any>(url, body, { headers: headers, params: params });
  }

  customPomReportUpload(payload: any) {
    const url = this.apiUrl + ApiUrls.customPomReportUpload;
    return this.http.post<any>(url, payload);
  }

  getAllDocAttachment(docId: any) {
    const url = this.apiUrl + ApiUrls.getAllDocAttachment + '/' + docId;
    return this.http.get<any>(url);
  }

  getAllDocPomReport(docId: any) {
    const url = this.apiUrl + ApiUrls.getAllDocPomReport + '/' + docId;
    return this.http.get<any>(url);
  }

  deleteDocAttachment(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteDocAttachment + '/' + docId;
    return this.http.post<any>(url, null);
  }

  deleteDocPomReport(docId: any) {
    const url = this.apiUrl + ApiUrls.deleteDocPomReport + '/' + docId;
    return this.http.post<any>(url, null);
  }

  GetSubjectsByDocumentType(docId: any) {
    const url = this.apiUrl + ApiUrls.getSelectedDocId + '/' + docId;
    return this.http.get<any>(url);
  }

  GetSubjectsDraftByDocumentType(docId: any, payload: any) {
    const url = this.apiUrl + ApiUrls.getSubjectDraftByDocType + '/' + docId;
    return this.http.post<any>(url, payload);
  }

  ParticipationNumberListOnSubject(body: any) {
    const url = this.apiUrl + ApiUrls.participationNumberListOnSubject;
    return this.http.post<any>(url, body);
  }

  GetAllDocSubjects(body: boolean) {
    const url = this.apiUrl + ApiUrls.getAllSelected;
    return this.http.post<any>(url, body);
  }

  GetDocSubjectsOfUser() {
    const url = this.apiUrl + ApiUrls.getSelectedSubject;
    return this.http.get<any>(url);
  }

  convertToDateToIso(dateString: any | string): Date | string {
    if (dateString != null && dateString != undefined && dateString !== '') {
      const year = dateString.year;
      const month = dateString.month - 1;
      const day = dateString.day;
      const utcDate = new Date(Date.UTC(year, month, day));
      const isoString = utcDate.toISOString();
      return isoString;
    }
    return '';
  }

  formatYear(value: any): number | string {
    if (!value) return '';
    // If the value is a Date object
    if (value instanceof Date) {
      return value.getFullYear();
    }

    // If the value is an ISO string
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.getFullYear();
      }
    }

    // If the value is already a valid 4-digit year
    if (typeof value === 'number' && value.toString().length === 4) {
      return value;
    }

    // Fallback
    console.warn('Invalid date format:', value);
    return '';
  }

  getTaskListByDocId(docId: any) {
    const url = this.apiUrl + ApiUrls.getTaskListByDocId + '/' + docId;
    return this.http.get<any>(url);
  }

  deleteDocument(body: any) {
    const url = this.apiUrl + ApiUrls.deleteDocument;
    return this.http.post<any>(url, body);
  }

  getDeletedDocuments(body: any) {
    const url = this.apiUrl + ApiUrls.getDeletedDocuments;
    return this.http.post<any>(url, body);
  }
  restoreDocument(docId: any) {
    const url = this.apiUrl + ApiUrls.restoreDocument + '/' + docId;
    return this.http.get<any>(url);
  }

  updateDocument(body: any) {
    const url = this.apiUrl + ApiUrls.updateDocument;
    return this.http.post<any>(url, body);
  }

  getDocumentTranslationDetails(docId: any) {
    const url = this.apiUrl + ApiUrls.documentTranslation + '/' + docId;
    return this.http.get<any>(url);
  }
  saveDocumentTranslation(body: any) {
    const url = this.apiUrl + ApiUrls.saveTranslation;
    return this.http.post<any>(url, body);
  }

  translateTheDoc(docId: any) {
    const url = this.apiUrl + ApiUrls.translateDoc + '/' + docId;
    return this.http.get<any>(url);
  }

  GetDocumentWorkflowRecord(body: any) {
    const url = this.apiUrl + ApiUrls.GetDocumentWorkflowRecord;
    return this.http.post<any>(url, body);
  }

  GetTopNotifications() {
    const url = this.apiUrl + ApiUrls.GetTopNotifications;
    return this.http.post<any>(url, null);
  }

  getExportLogData(body: any) {
    const url = this.apiUrl + ApiUrls.exportLogData;
    return this.http.post(url, body, { responseType: 'blob' as 'json' });
  }

  getTaskExportLogData(body: any) {
    const url = this.apiUrl + ApiUrls.exportTaskLogData;
    return this.http.post(url, body, { responseType: 'blob' as 'json' });
  }

  getExportDevelopment(body: any): Observable<Blob> {
    const url = this.apiUrl + ApiUrls.exportdevelopment;
    return this.http.post<Blob>(url, body, { responseType: 'blob' as 'json' });
  }

  shareDevelopment(body: any) {
    const url = this.apiUrl + ApiUrls.sharedevelopment;
    return this.http.post<any>(url, body);
  }

  shareIdentificationCard(body: any) {
    const url = this.apiUrl + ApiUrls.shareIdentificationCard;
    return this.http.post<any>(url, body);
  }

  shareAdminInboxData(body: any) {
    const url = this.apiUrl + ApiUrls.shareAdminInbox;
    return this.http.post<any>(url, body);
  }

  sharePersonalInboxData(body: any) {
    const url = this.apiUrl + ApiUrls.sharePersonalInbox;
    return this.http.post<any>(url, body);
  }

  shareReport(body: any) {
    const url = this.apiUrl + ApiUrls.shareReport;
    return this.http.post<any>(url, body);
  }

  downloadDocument(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadDocument;
    return this.http.post(url, requestObject, { responseType: 'blob' });
  }

  addNewLiaisonOfficer(body: any) {
    const url = this.apiUrl + ApiUrls.saveLiaisonOfficers;
    return this.http.post<any>(url, body);
  }

  addNewParticipants(body: any) {
    const url = this.apiUrl + ApiUrls.saveParticipants;
    return this.http.post<any>(url, body);
  }

  getLiaisonOfficerById(docId: any) {
    const url = this.apiUrl + ApiUrls.getLiaisonOfficerById + '/' + docId;
    return this.http.get<any>(url);
  }

  getParticipantsById(docId: any) {
    const url = this.apiUrl + ApiUrls.getParticipantsById + '/' + docId;
    return this.http.get<any>(url);
  }

  deleteLiaisonOfficer(docId: number, userId: number) {
    const link = `${this.apiUrl}/api/Document/DeleteLiaisonOfficer/${docId}/${userId}`;
    return this.http.post<any>(link, {});
  }

  deleteParticipant(docId: number, userId: number) {
    const link = `${this.apiUrl}/api/Document/DeleteParticipationNames/${docId}/${userId}`;
    return this.http.post<any>(link, {});
  }

  downloadIdentificationCard(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.downloadIdentificationCard;
    return this.http.post(url, requestObject, { responseType: 'blob' });
  }

  updateIdCard(body: any) {
    const url = this.apiUrl + ApiUrls.updateIdCard;
    return this.http.post<any>(url, body);
  }

  getIdCardByDocId(payload: any) {
    const url = this.apiUrl + ApiUrls.getIdCardByDocId;
    return this.http.post<any>(url, payload);
  }

  getProcessDocumentStatus(docId: any) {
    const url = this.apiUrl + ApiUrls.getDocumentProcessingStatus;
    const params = new HttpParams().set('Id', docId);
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.get<any>(url, { params, headers });
  }
  ExportSingleReport(requestObject: any): Observable<any> {
    const url = this.apiUrl + ApiUrls.exportSingleReport;
    return this.http.post(url, requestObject, { responseType: 'blob' });
  }
  getPOMReportTemplateById(docId: any) {
    const url = this.apiUrl + ApiUrls.getPOMReportTemplateById + '/' + docId;
    return this.http.get<any>(url);
  }

  // startBackgroundPolling(documentId: string, callback: (status: string) => void) {
  //   this.statusSubscription = interval(10000).pipe(
  //     switchMap(() =>
  //       this.getProcessDocumentStatus(documentId).pipe(
  //         catchError((err) => {
  //           console.error('Polling error:', err);
  //           return of(null);
  //         })
  //       )
  //     )
  //   ).subscribe((res) => {
  //     if (res?.status && res?.data?.isDocumentProcessed) {
  //       callback('completed');
  //       this.stopBackgroundPolling();
  //     }
  //   });
  // }

  startBackgroundPolling(
    documentId: string,
    callback: (status: 'completed' | 'timeout' | 'processError') => void
  ) {
    const timeout$ = timer(this.processTimer);
    this.statusSubscription = interval(this.processDocInterval)
      .pipe(
        takeUntil(timeout$),
        switchMap(() =>
          this.getProcessDocumentStatus(documentId).pipe(
            catchError((err) => {
              console.error('Polling error:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: (res) => {
          if (
            res?.status &&
            res?.data?.isDocumentProcessed &&
            !res?.data?.isDocumentFailed
          ) {
            callback('completed');
            this.stopBackgroundPolling();
          } else if (
            res?.status &&
            res?.data?.isDocumentProcessed &&
            res?.data?.isDocumentFailed
          ) {
            callback('processError');
            this.stopBackgroundPolling();
          }
        },
        complete: () => {
          callback('timeout');
          this.stopBackgroundPolling();
        },
      });
  }

  stopBackgroundPolling() {
    this.statusSubscription?.unsubscribe();
    this.statusSubscription = null;
  }

  getPersonalInboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetPersonalInboxCount`;
    return this.http.post<any>(url, payload);
  }

  getPersonalOutboxCount(): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetPersonalOutboxCount`;
    return this.http.get<any>(url);
  }

  getAdminInboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetAdminInboxCount`;
    return this.http.post<any>(url, payload);
  }

  getAdminOutboxCount(payload: any): Observable<any> {
    const url = `${this.apiUrl}/api/WorkFlowDetails/GetAdminOutboxCount`;
    return this.http.post<any>(url, payload);
  }

  updateIsReadNotifications(body: any) {
    const link = this.apiUrl + ApiUrls.updateIsReadNotifications;
    return this.http.post<any>(link, body);
  }

  updateEmployee(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateEmployee;
    return this.http.post<any>(link, body);
  }

  getEvents(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getEvents;
    return this.http.post<any>(link, body).pipe(
      map((response: any) => this.attachEventCalendarDimensions(response))
    );
  }

  createEvent(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.createEvent;
    return this.http.post<any>(link, body);
  }

  updateEvent(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateEvent;
    return this.http.post<any>(link, body);
  }

  deleteEvent(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.deleteEvent;
    return this.http.post<any>(link, body);
  }



  activeEvent(eventId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.activeEvent + '/' + eventId;
    return this.http.post<any>(link, null);
  }

  deActivateEvent(eventId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.deActivateEvent + '/' + eventId;
    return this.http.post<any>(link, null);
  }

  // NEW URLS FOR DASHBOARD

  getTotalCountStatistics(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getTotalCountStatistics;
    return this.http.post<any>(link, payload, {
      headers: { 'X-Skip-Loader': 'true' },
    });
  }

  getRecentUploads(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getRecentUploads;
    return this.http.post<any>(link, payload, {
      headers: { 'X-Skip-Loader': 'true' },
    });
  }

  getMySharedItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getMySharedItems;
    return this.http.post<any>(link, payload, {
      headers: { 'X-Skip-Loader': 'true' },
    });
  }

  getActivityFeed(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getActivityFeed;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getUserStorage(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.userStorage;
    return this.http.post<any>(link, payload, {
      headers: { 'X-Skip-Loader': 'true' },
    });
  }

  getAllActions(): Observable<any> {
    const link = this.apiUrl + ApiUrls.getAllActions;
    return this.http.get<any>(link, {
      headers: { 'X-Skip-Loader': 'true' },
    });
  }

  getActivityLog(payload?: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getActivityLog;
    const request = payload || { pageIndex: 0, pageSize: 10 };
    return this.http.post<any>(link, request);
  }

  getFolderItem(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getFolderItem;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getMyLibraryItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getMyLibraryItems;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getAllFilesInSystem(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getAllFilesInSystem;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getSharedFolderItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedFolderItems;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  /** Browse-by-tag at library root (direct TagMapping by tagIds; matches sidebar counts). */
  browseFileByTag(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.browseFileByTag;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  /** Browse-by-event at library root (direct EventId; matches sidebar counts). */
  browseFileByEvent(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.browseFileByEvent;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getFolderHierarchy(userId: number, maxDepth?: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getFolderHierarchy;
    let params = new HttpParams().set('userId', userId);
    if (maxDepth !== undefined && maxDepth !== null) {
      params = params.set('maxDepth', maxDepth);
    }
    return this.http
      .post(link, {}, { params, responseType: 'text' })
      .pipe(map((raw) => this.parseJsonSafely(raw)));
  }

  getSubFolderHierarchy(userId: number, folderId: number, maxDepth?: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSubFolderHierarchy;
    let params = new HttpParams().set('userId', userId).set('folderId', folderId);
    if (maxDepth !== undefined && maxDepth !== null) {
      params = params.set('maxDepth', maxDepth);
    }
    return this.http
      .post(link, {}, { params, responseType: 'text' })
      .pipe(map((raw) => this.parseJsonSafely(raw)));
  }

  /** Department / Org Assets library tree (librarytype = 2); scoped by backend from JWT. Optional filter for full-access users. */
  getDepartmentLibraryFolderHierarchy(maxDepth?: number, filterDepartmentId?: number | null): Observable<any> {
    const link = this.apiUrl + ApiUrls.getDepartmentLibraryFolderHierarchy;
    let params = new HttpParams();
    if (maxDepth !== undefined && maxDepth !== null) {
      params = params.set('maxDepth', maxDepth);
    }
    if (filterDepartmentId !== undefined && filterDepartmentId !== null && Number(filterDepartmentId) > 0) {
      params = params.set('filterDepartmentId', Number(filterDepartmentId));
    }
    return this.http
      .post(link, {}, { params, responseType: 'text' })
      .pipe(map((raw) => this.parseJsonSafely(raw)));
  }

  getDepartmentRootFolderId(departmentId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getDepartmentRootFolderId;
    const params = new HttpParams().set('departmentId', Number(departmentId));
    return this.http
      .post(link, {}, { params, responseType: 'text' })
      .pipe(map((raw) => this.parseJsonSafely(raw)));
  }

  getDepartmentLibrarySubFolderHierarchy(
    folderId: number,
    maxDepth?: number,
    filterDepartmentId?: number | null
  ): Observable<any> {
    const link = this.apiUrl + ApiUrls.getDepartmentLibrarySubFolderHierarchy;
    let params = new HttpParams().set('folderId', folderId);
    if (maxDepth !== undefined && maxDepth !== null) {
      params = params.set('maxDepth', maxDepth);
    }
    if (filterDepartmentId !== undefined && filterDepartmentId !== null && Number(filterDepartmentId) > 0) {
      params = params.set('filterDepartmentId', Number(filterDepartmentId));
    }
    return this.http
      .post(link, {}, { params, responseType: 'text' })
      .pipe(map((raw) => this.parseJsonSafely(raw)));
  }

  private parseJsonSafely(raw: string): any {
    if (!raw) {
      return raw;
    }

    try {
      return JSON.parse(raw);
    } catch {
      const sanitized = raw.replace(/[\u0000-\u001F\u007F]/g, '');
      try {
        return JSON.parse(sanitized);
      } catch {
        // Escape invalid backslashes if the response contains broken escape sequences.
        const fixed = sanitized.replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
        return JSON.parse(fixed);
      }
    }
  }

  getFolderById(folderId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getFolderById + folderId;
    return this.http.get<any>(link);
  }

  getFolderTags(folderId: number): Observable<any> {
    const link = this.apiUrl + `/api/Folder/getTags/` + folderId;
    return this.http.get<any>(link);
  }

  updateFolder(folderId: number, body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateFolder + folderId;
    return this.http.post<any>(link, body);
  }

  getFileFromCloud(fileId: any): Observable<Blob> {
    const link = `${this.apiUrl}${ApiUrls.getFileFromCloud}${fileId}`;
    if (this.isResumableDownloadEnabled()) {
      // Preview / in-app open: keep in memory (no save picker).
      return this.downloadUrlInChunks(link, { streamToDisk: false }).pipe(
        filter((e): e is HttpResponse<Blob> => e.type === HttpEventType.Response),
        map((e) => e.body as Blob)
      );
    }
    return this.http.get(link, { responseType: 'blob' });
  }

  getFileFromCloudWithProgress(
    fileId: any,
    options?: { fileName?: string; streamToDisk?: boolean }
  ): Observable<HttpEvent<Blob>> {
    const link = `${this.apiUrl}${ApiUrls.getFileFromCloud}${fileId}`;
    if (this.isResumableDownloadEnabled()) {
      return this.downloadUrlInChunks(link, {
        fileName: options?.fileName || `file-${fileId}`,
        streamToDisk: options?.streamToDisk ?? true,
      });
    }
    return this.http.get(link, {
      responseType: 'blob',
      observe: 'events',
      reportProgress: true
    });
  }

  private isResumableDownloadEnabled(): boolean {
    const cfg = this.config.resumableDownload;
    if (cfg === true) {
      return true;
    }
    if (cfg && typeof cfg === 'object') {
      return !!(cfg as ResumableDownloadConfig).enabled;
    }
    return false;
  }

  private isStreamToDiskEnabled(): boolean {
    const cfg = this.config.resumableDownload;
    if (!this.isResumableDownloadEnabled()) {
      return false;
    }
    if (cfg && typeof cfg === 'object') {
      return (cfg as ResumableDownloadConfig).streamToDisk !== false;
    }
    return true;
  }

  private getResumableDownloadConfig(): Required<
    Pick<
      ResumableDownloadConfig,
      'chunkSizeBytes' | 'maxRetries' | 'maxConcurrentChunks' | 'speedMeasurementInterval'
    >
  > {
    const cfg = this.config.resumableDownload;
    const objectCfg = cfg && typeof cfg === 'object' ? (cfg as ResumableDownloadConfig) : null;
    const cap = objectCfg?.maxConcurrentChunks ?? 5;
    return {
      chunkSizeBytes: objectCfg?.chunkSizeBytes ?? 5 * 1024 * 1024,
      maxRetries: objectCfg?.maxRetries ?? 3,
      // Same ceiling as UploadManagerService veryFast (5).
      maxConcurrentChunks: Math.max(1, Math.min(cap, 5)),
      speedMeasurementInterval: objectCfg?.speedMeasurementInterval ?? 5,
    };
  }

  /**
   * Same Mbps bands as UploadManagerService.updateUploadSpeed for concurrent chunks,
   * plus adaptive Range chunk size (upload keeps a fixed session chunk size; download can resize).
   *
   *   slow    < 1 Mbps  → concurrent 2, chunk 1MB
   *   medium  1–5 Mbps  → concurrent 3, chunk 2MB
   *   fast    5–20 Mbps → concurrent 4, chunk = base (typically 5MB)
   *   veryFast ≥ 20 Mbps → concurrent 5, chunk ≥ 8MB
   */
  private classifyDownloadNetSpeed(
    bytesPerSecond: number,
    baseChunkSizeBytes: number,
    maxCap: number
  ): {
    speed: 'slow' | 'medium' | 'fast' | 'veryFast';
    concurrentChunks: number;
    chunkSizeBytes: number;
    mbps: number;
  } {
    const mbps = bytesPerSecond / (1024 * 1024);
    let speed: 'slow' | 'medium' | 'fast' | 'veryFast';
    let concurrentChunks: number;
    let chunkSizeBytes: number;

    if (mbps < 1) {
      speed = 'slow';
      concurrentChunks = 2;
      chunkSizeBytes = Math.min(baseChunkSizeBytes, 1 * 1024 * 1024);
    } else if (mbps < 5) {
      speed = 'medium';
      concurrentChunks = 3;
      chunkSizeBytes = Math.min(baseChunkSizeBytes, 2 * 1024 * 1024);
    } else if (mbps < 20) {
      speed = 'fast';
      concurrentChunks = 4;
      chunkSizeBytes = baseChunkSizeBytes;
    } else {
      speed = 'veryFast';
      concurrentChunks = 5;
      chunkSizeBytes = Math.max(baseChunkSizeBytes, 8 * 1024 * 1024);
    }

    concurrentChunks = Math.max(1, Math.min(concurrentChunks, maxCap));
    return { speed, concurrentChunks, chunkSizeBytes, mbps };
  }

  /**
   * Downloads a URL in HTTP Range chunks with retry (upload-like).
   * Prefer writing each chunk to disk via File System Access API when enabled;
   * otherwise concatenates chunks in memory into one Blob.
   * Emits HttpEvent DownloadProgress + final Response for drop-in use with existing UI.
   *
   * When Content-Range total is known, remaining ranges download in an adaptive
   * parallel pool (Range GETs are independent — unlike GCS resumable upload).
   */
  private downloadUrlInChunks(
    url: string,
    options?: DownloadStreamOptions | number
  ): Observable<HttpEvent<Blob>> {
    const opts: DownloadStreamOptions =
      typeof options === 'number' ? { knownTotalBytes: options } : options || {};

    return defer(() => {
      this.downloadActivity.begin();
      return from(
        (async (): Promise<FileSystemWritableFileStreamLike | null> => {
          if (opts.writable) {
            return opts.writable;
          }
          if (opts.streamToDisk === false || !this.isStreamToDiskEnabled()) {
            return null;
          }
          return openDiskWritable(opts.fileName || 'download');
        })()
      ).pipe(
        switchMap((writable) => this.runChunkedDownload(url, opts, writable)),
        finalize(() => this.downloadActivity.end())
      );
    });
  }

  private async fetchDownloadRange(
    url: string,
    start: number,
    end: number
  ): Promise<HttpResponse<ArrayBuffer>> {
    return firstValueFrom(
      this.http.get(url, {
        headers: new HttpHeaders({
          Range: `bytes=${start}-${end}`,
          'X-Skip-Loader': 'true',
        }),
        observe: 'response',
        responseType: 'arraybuffer',
      })
    );
  }

  private runChunkedDownload(
    url: string,
    opts: DownloadStreamOptions,
    writable: FileSystemWritableFileStreamLike | null
  ): Observable<HttpEvent<Blob>> {
    return new Observable<HttpEvent<Blob>>((subscriber) => {
      let cancelled = false;
      const { chunkSizeBytes, maxRetries, maxConcurrentChunks, speedMeasurementInterval } =
        this.getResumableDownloadConfig();

      const abortWritable = async () => {
        if (writable) {
          try {
            await writable.abort();
          } catch {
            /* ignore */
          }
          writable = null;
        }
      };

      const run = async () => {
        let total = opts.knownTotalBytes && opts.knownTotalBytes > 0 ? opts.knownTotalBytes : 0;
        let loaded = 0;
        let contentType = 'application/octet-stream';
        const downloadStartedAt = Date.now();

        // In-memory slots by chunk index (supports out-of-order completion).
        const memoryChunks: ArrayBuffer[] = [];

        let speedBytes = 0;
        let speedChunkCount = 0;
        let speedWindowStart = Date.now();
        let currentChunkSize = chunkSizeBytes;
        let targetConcurrency = 2; // upload default band starts at slow=2
        // Parallel fetches are fine; FS writable streams need serialized writes.
        let writeChain: Promise<void> = Promise.resolve();

        const emitProgress = () => {
          subscriber.next({
            type: HttpEventType.DownloadProgress,
            loaded,
            total: total > 0 ? total : undefined,
          } as HttpEvent<Blob>);
        };

        const applyNetSpeed = (bytesPerSecond: number) => {
          const classified = this.classifyDownloadNetSpeed(
            bytesPerSecond,
            chunkSizeBytes,
            maxConcurrentChunks
          );
          targetConcurrency = classified.concurrentChunks;
          currentChunkSize = classified.chunkSizeBytes;
          console.log(
            `[SPEED] Download: ${classified.speed} (${classified.mbps.toFixed(2)} Mbps) - ` +
              `Chunks: ${targetConcurrency}, ChunkSize: ${(currentChunkSize / (1024 * 1024)).toFixed(1)}MB`
          );
        };

        const noteSpeed = (byteLength: number) => {
          speedBytes += byteLength;
          speedChunkCount++;
          if (speedChunkCount % speedMeasurementInterval === 0) {
            const elapsed = (Date.now() - speedWindowStart) / 1000;
            if (elapsed > 0 && speedBytes > 0) {
              applyNetSpeed(speedBytes / elapsed);
            }
            speedBytes = 0;
            speedWindowStart = Date.now();
          }
        };

        const writeChunk = async (start: number, index: number, body: ArrayBuffer) => {
          if (writable) {
            const data = new Uint8Array(body);
            const position = start;
            writeChain = writeChain.then(() =>
              writable!.write({ type: 'write', position, data })
            );
            await writeChain;
          } else {
            memoryChunks[index] = body;
          }
          loaded += body.byteLength;
          emitProgress();
          noteSpeed(body.byteLength);
        };

        try {
          // --- First chunk: discover total size (Content-Range) before parallelizing ---
          const firstEnd = chunkSizeBytes - 1;
          let firstResponse: HttpResponse<ArrayBuffer> | null = null;
          let firstError: unknown;

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            if (cancelled) {
              await abortWritable();
              return;
            }
            try {
              firstResponse = await this.fetchDownloadRange(url, 0, firstEnd);
              firstError = null;
              break;
            } catch (err: any) {
              firstError = err;
              console.warn('[ShareTransfer] range chunk failed', {
                at: new Date().toISOString(),
                phase: 'first-chunk',
                url,
                range: `0-${firstEnd}`,
                attempt: attempt + 1,
                maxRetries,
                elapsedMs: Date.now() - downloadStartedAt,
                status: err?.status ?? null,
                message: err?.message ?? String(err),
                name: err?.name ?? null,
              });
              if (attempt < maxRetries - 1) {
                await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
              }
            }
          }

          if (firstError) {
            throw firstError;
          }
          if (!firstResponse?.body) {
            throw new Error('Empty download response');
          }

          // Keep the server's Content-Disposition (file-table name) on the final
          // response so callers save with the exact backend filename.
          const contentDisposition = firstResponse.headers.get('Content-Disposition');
          const finalHeaders = contentDisposition
            ? new HttpHeaders({ 'Content-Disposition': contentDisposition })
            : undefined;

          // Server ignored Range and returned the full object.
          if (firstResponse.status === 200) {
            contentType = firstResponse.headers.get('Content-Type') || contentType;
            if (writable) {
              await writable.write(new Uint8Array(firstResponse.body));
              await writable.close();
              writable = null;
              subscriber.next({
                type: HttpEventType.DownloadProgress,
                loaded: firstResponse.body.byteLength,
                total: firstResponse.body.byteLength,
              } as HttpEvent<Blob>);
              subscriber.next(
                new HttpResponse<Blob>({ body: createDiskSavedBlob(), status: 200, headers: finalHeaders })
              );
              subscriber.complete();
              return;
            }
            const fullBlob = new Blob([firstResponse.body], { type: contentType });
            subscriber.next({
              type: HttpEventType.DownloadProgress,
              loaded: firstResponse.body.byteLength,
              total: firstResponse.body.byteLength,
            } as HttpEvent<Blob>);
            subscriber.next(
              new HttpResponse<Blob>({ body: fullBlob, status: 200, headers: finalHeaders })
            );
            subscriber.complete();
            return;
          }

          const headerType = firstResponse.headers.get('Content-Type');
          if (headerType) {
            contentType = headerType;
          }

          const contentRange = firstResponse.headers.get('Content-Range');
          if (contentRange) {
            const match = /bytes\s+(\d+)-(\d+)\/(\d+|\*)/i.exec(contentRange);
            if (match && match[3] !== '*') {
              total = Number(match[3]);
            }
          }

          await writeChunk(0, 0, firstResponse.body);

          if (total > 0 && loaded >= total) {
            // Single-chunk file
          } else if (total <= 0) {
            // No Content-Range total — fall back to sequential append for the rest.
            let start =
              contentRange && /bytes\s+\d+-(\d+)\//i.exec(contentRange)
                ? Number(/bytes\s+\d+-(\d+)\//i.exec(contentRange)![1]) + 1
                : firstResponse.body.byteLength;

            while (!cancelled) {
              const end = start + currentChunkSize - 1;
              let response: HttpResponse<ArrayBuffer> | null = null;
              let lastError: unknown;

              for (let attempt = 0; attempt < maxRetries; attempt++) {
                if (cancelled) {
                  await abortWritable();
                  return;
                }
                try {
                  response = await this.fetchDownloadRange(url, start, end);
                  lastError = null;
                  break;
                } catch (err: any) {
                  lastError = err;
                  if (err?.status === 416 && loaded > 0) {
                    response = null;
                    lastError = null;
                    break;
                  }
                  if (attempt < maxRetries - 1) {
                    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
                  }
                }
              }

              if (lastError) {
                throw lastError;
              }
              if (!response?.body || response.body.byteLength === 0) {
                break;
              }

              const ct = response.headers.get('Content-Type');
              if (ct) {
                contentType = ct;
              }
              const cr = response.headers.get('Content-Range');
              if (cr) {
                const m = /bytes\s+(\d+)-(\d+)\/(\d+|\*)/i.exec(cr);
                if (m && m[3] !== '*') {
                  total = Number(m[3]);
                }
              }

              if (writable) {
                await writable.write(new Uint8Array(response.body));
              } else {
                memoryChunks.push(response.body);
              }
              loaded += response.body.byteLength;
              emitProgress();
              noteSpeed(response.body.byteLength);

              if (total > 0 && loaded >= total) {
                break;
              }

              const endMatch = cr ? /bytes\s+(\d+)-(\d+)\//i.exec(cr) : null;
              const rangeEnd = endMatch
                ? Number(endMatch[2])
                : start + response.body.byteLength - 1;
              start = rangeEnd + 1;

              if (total > 0 && start >= total) {
                break;
              }
              if (!cr && response.body.byteLength < currentChunkSize) {
                break;
              }
            }
          } else {
            // Parallel adaptive pool — ranges are pulled with the live chunk size so
            // net-speed updates can shrink/grow subsequent Ranges (unlike upload sessions).
            type ChunkJob = { index: number; start: number; end: number; attempts: number };
            const firstRangeMatch = contentRange
              ? /bytes\s+(\d+)-(\d+)\//i.exec(contentRange)
              : null;
            const firstRangeEnd = firstRangeMatch
              ? Number(firstRangeMatch[2])
              : firstResponse.body.byteLength - 1;

            let nextStart = firstRangeEnd + 1;
            let nextIndex = 1;
            const retryQueue: ChunkJob[] = [];

            const takeNextJob = (): ChunkJob | null => {
              if (retryQueue.length > 0) {
                return retryQueue.shift()!;
              }
              if (nextStart >= total) {
                return null;
              }
              const start = nextStart;
              const end = Math.min(start + currentChunkSize - 1, total - 1);
              nextStart = end + 1;
              return { index: nextIndex++, start, end, attempts: 0 };
            };

            // Seed concurrency + chunk size from the first-chunk window (same as upload measure).
            {
              const elapsed = (Date.now() - speedWindowStart) / 1000;
              if (elapsed > 0 && speedBytes > 0) {
                applyNetSpeed(speedBytes / elapsed);
              } else {
                targetConcurrency = Math.min(2, maxConcurrentChunks);
                currentChunkSize = Math.min(chunkSizeBytes, 1 * 1024 * 1024);
              }
              speedBytes = 0;
              speedWindowStart = Date.now();
            }

            let running = 0;
            let fatalError: unknown = null;

            await new Promise<void>((resolve, reject) => {
              const schedule = () => {
                if (cancelled) {
                  resolve();
                  return;
                }
                if (fatalError) {
                  reject(fatalError);
                  return;
                }

                while (running < targetConcurrency && !fatalError && !cancelled) {
                  const job = takeNextJob();
                  if (!job) {
                    break;
                  }
                  running++;
                  (async () => {
                    try {
                      const response = await this.fetchDownloadRange(url, job.start, job.end);
                      if (!response?.body) {
                        throw new Error(`Empty body for range ${job.start}-${job.end}`);
                      }
                      if (response.status === 416) {
                        return;
                      }
                      const ct = response.headers.get('Content-Type');
                      if (ct) {
                        contentType = ct;
                      }
                      await writeChunk(job.start, job.index, response.body);
                    } catch (err: any) {
                      if (err?.status === 416 && loaded > 0) {
                        return;
                      }
                      job.attempts++;
                      if (job.attempts >= maxRetries) {
                        fatalError = err;
                        return;
                      }
                      await new Promise((r) => setTimeout(r, 400 * job.attempts));
                      // Re-queue so any worker can pick it up (don't stall the pool).
                      retryQueue.push(job);
                    } finally {
                      running--;
                      schedule();
                    }
                  })();
                }

                if (retryQueue.length === 0 && nextStart >= total && running === 0) {
                  resolve();
                }
              };

              schedule();
            });
          }

          if (cancelled) {
            await abortWritable();
            return;
          }

          if (writable) {
            await writable.close();
            writable = null;
            subscriber.next(
              new HttpResponse<Blob>({ body: createDiskSavedBlob(), status: 200, headers: finalHeaders })
            );
          } else {
            for (let i = 0; i < memoryChunks.length; i++) {
              if (!memoryChunks[i]) {
                throw new Error(`Missing download chunk ${i}`);
              }
            }
            const finalBlob = new Blob(memoryChunks, { type: contentType });
            subscriber.next(
              new HttpResponse<Blob>({ body: finalBlob, status: 200, headers: finalHeaders })
            );
          }
          subscriber.complete();
        } catch (err) {
          await abortWritable();
          const elapsedMs = Date.now() - downloadStartedAt;
          const elapsedSec = Math.round((elapsedMs / 1000) * 10) / 10;
          const suspectedIngressTimeout =
            [30, 60, 120].some((t) => Math.abs(elapsedSec - t) <= 3) ||
            (elapsedSec >= 25 && elapsedSec <= 35);
          console.error('[ShareTransfer] chunked download failed', {
            at: new Date().toISOString(),
            url,
            loaded,
            total: total || null,
            progressPercent: total > 0 ? Math.round((loaded / total) * 1000) / 10 : null,
            elapsedMs,
            elapsedSec,
            suspectedIngressTimeout,
            timeoutHint: suspectedIngressTimeout
              ? `Failure at ~${elapsedSec}s — likely load-balancer/ingress response timeout (check BackendConfig timeoutSec).`
              : null,
            status: (err as any)?.status ?? null,
            message: (err as any)?.message ?? String(err),
            name: (err as any)?.name ?? null,
          });
          throw err;
        }
      };

      run().catch((err) => {
        if (!cancelled) {
          subscriber.error(err);
        }
      });

      return () => {
        cancelled = true;
      };
    });
  }


  getTags(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getTags;
    return this.http.post<any>(link, payload);
  }

  shareFile(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.shareFile;
    return this.http.post<any>(link, payload);
  }

  getSharedItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedItems;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getAllFileFolderPermissions(): Observable<any> {
    const link = this.apiUrl + ApiUrls.getAllFileFolderPermissions;
    return this.http.get<any>(link, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  shareFolder(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.shareFolder;
    return this.http.post<any>(link, payload);
  }

  createAnonymousShareLink(payload: {
    fileId: number;
    folderId: number;
    password: string;
    allowDownload: boolean;
    /** ISO 8601, e.g. 2026-03-24T10:51:12.265Z */
    expiryDate: string;
  }): Observable<any> {
    const link = this.apiUrl + ApiUrls.anonymousShare;
    return this.http.post<any>(link, payload);
  }

  getCurrentAnonymousShareLink(fileId: number, folderId: number): Observable<any> {
    const query = `?fileId=${fileId || 0}&folderId=${folderId || 0}`;
    const link = this.apiUrl + ApiUrls.anonymousShareCurrent + query;
    return this.http.get<any>(link);
  }

  // Temporarily disabled — /api/AnonymousShare/anonymous-share/links
  // getAnonymousShareLinks(fileId: number, folderId: number): Observable<any> {
  //   const query = `?fileId=${fileId || 0}&folderId=${folderId || 0}`;
  //   const link = this.apiUrl + ApiUrls.anonymousShareLinks + query;
  //   return this.http.get<any>(link);
  // }

  updateAnonymousShareLink(payload: {
    fileId: number;
    folderId: number;
    /** When set, updates this link row (extend expiry / password / download flag). */
    linkId?: number;
    password?: string | null;
    usePassword: boolean;
    allowDownload: boolean;
    expiryDate: string;
  }): Observable<any> {
    const link = this.apiUrl + ApiUrls.anonymousShare;
    return this.http.put<any>(link, payload);
  }

  deactivateAnonymousShareLink(payload: {
    fileId: number;
    folderId: number;
    /** When set, revokes this specific link row. */
    linkId?: number;
  }): Observable<any> {
    const link = this.apiUrl + ApiUrls.anonymousShareDeactivate;
    return this.http.post<any>(link, payload);
  }

  /** Verify token — returns mediaType, isProtected, allowDownload, etc. */
  verifyAnonymousShareToken(token: string): Observable<any> {
    const link =
      this.apiUrl +
      ApiUrls.anonymousShareVerifyToken +
      `?token=${encodeURIComponent(token)}`;
    return this.http.get<any>(link);
  }

  /** (File): get full file details by token (after verifyToken or password verify). */
  getAnonymousShareFileByToken(token: string, password?: string): Observable<any> {
    let link =
      this.apiUrl +
      ApiUrls.anonymousShareGetFileById +
      `?token=${encodeURIComponent(token)}`;
    if (password != null && password !== '') {
      link += `&password=${encodeURIComponent(password)}`;
    }
    return this.http.get<any>(link);
  }

  /** (Folder): get folder + contained files by token, folderId, and optional password. */
  getAnonymousShareFileByFolderId(token: string, folderId: number, password?: string): Observable<any> {
    let link =
      this.apiUrl +
      ApiUrls.anonymousShareGetFileByFolderId +
      `?token=${encodeURIComponent(token)}&folderId=${folderId}`;
    if (password != null && password !== '') {
      link += `&password=${encodeURIComponent(password)}`;
    }
    return this.http.get<any>(link);
  }

  /** Get library items (files) inside a shared folder. */
  getAnonymousShareMyLibraryItems(payload: {
    fileId?: number;
    folderId: number;
    password?: string;
    allowDownload?: boolean;
    expiryDate?: string;
    token: string;
    pageIndex?: number;
    pageSize?: number;
    tagIds?: number[];
    eventIds?: number[];
    sortCriteria?: { field: string; order: string; priority: number }[];
  }): Observable<any> {
    const link = this.apiUrl + ApiUrls.anonymousShareGetMyLibraryItems;
    return this.http.post<any>(link, payload);
  }

  /**
   * Verify password for a protected share link.
   * POST `/api/AnonymousShare/anonymous-share/verify?token=&password=`
   */
  verifyAnonymousShare(payload: { token: string; password: string }): Observable<any> {
    const q = `?token=${encodeURIComponent(payload.token)}&password=${encodeURIComponent(
      payload.password ?? ''
    )}`;
    const link = this.apiUrl + ApiUrls.anonymousShareVerify + q;
    return this.http.post<any>(link, {});
  }

  /** Download a single shared file (returns blob). */
  downloadAnonymousShareFile(token: string): Observable<Blob> {
    const link =
      this.apiUrl +
      ApiUrls.anonymousShareDownloadFile +
      `?token=${encodeURIComponent(token)}`;
    return this.http.get(link, { responseType: 'blob' });
  }

  /** Download an entire shared folder (returns blob, typically zip). */
  downloadAnonymousShareFolder(token: string): Observable<Blob> {
    const link =
      this.apiUrl +
      ApiUrls.anonymousShareDownloadFolder +
      `?token=${encodeURIComponent(token)}`;
    return this.http.get(link, { responseType: 'blob' });
  }

  revertFileShare(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.revertFileShare;
    return this.http.post<any>(link, payload);
  }

  revertFolderShare(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.revertFolderShare;
    return this.http.post<any>(link, payload);
  }

  getUserGuide(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getUserGuide;
    return this.http.post<any>(link, body);
  }

  downloadUserGuide(id: any) {
    const url = this.apiUrl + ApiUrls.downloadUserGuide + '/' + id;
    return this.http.get(url, { responseType: 'blob' });
  }


  uploadUserGuide(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const link = this.apiUrl + ApiUrls.uploadUserGuide;
    return this.http.post<any>(link, formData, { headers });
  }

  updateUserGuide(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({
      'content-type': 'multipart/form-data',
    });
    const link = this.apiUrl + ApiUrls.updateUserGuide;
    return this.http.post<any>(link, formData, { headers });
  }

  getSharedFileUser(fileId: number, payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedFileUser + fileId;
    return this.http.post<any>(link, payload);
  }

  getSharedFolderUser(folderId: number, payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedFolderUser + folderId;
    return this.http.post<any>(link, payload);
  }

  getViewDownloadLogs(entityType: 'file' | 'folder', entityId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getViewDownloadLogs + `${entityType}/${entityId}`;
    return this.http.get<any>(link);
  }

  getSharedFileDepartments(fileId: number, payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedFileDepartments + fileId;
    return this.http.post<any>(link, payload);
  }

  getSharedFolderDepartments(folderId: number, payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSharedFolderDepartments + folderId;
    return this.http.post<any>(link, payload);
  }

  updateFileShare(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateFileShare;
    return this.http.post<any>(link, payload);
  }

  updateFolderShare(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateFolderShare;
    return this.http.post<any>(link, payload);
  }

  private static readonly FALLBACK_ZIP_MAX_BYTES = 1024 * 1024 * 1024; // 1 GB

  private folderManifest$(manifestUrl: string) {
    return this.http.get<{
      folderName: string;
      files: { fileId: number; relativePath: string; fileName: string; size: number }[];
    }>(manifestUrl, { headers: { 'X-Skip-Loader': 'true' } });
  }

  private downloadFolderTreeToDisk(
    manifestUrl: string,
    buildFileUrl: (fileId: number) => string,
    zipFallback: {
      prepareUrl: string;
      statusUrl: (downloadId: string) => string;
      preparedUrl: (downloadId: string) => string;
      suggestedName: string;
    }
  ): Observable<HttpEvent<Blob>> {
    if (isFsaSupported()) {
      return this.downloadFolderTreeToDirectory(manifestUrl, buildFileUrl);
    }
    return this.downloadFolderViaPreparedZip(manifestUrl, zipFallback);
  }

  private sanitizeFsName(name: string): string {
    const cleaned = (name || '').replace(/[\\/:*?"<>|]/g, '_').trim();
    return cleaned && cleaned !== '.' && cleaned !== '..' ? cleaned : '_';
  }

  private async ensureDirectory(
    root: any,
    relativeDirPath: string,
    cache: Map<string, any>
  ): Promise<any> {
    if (!relativeDirPath) {
      return root;
    }
    const hit = cache.get(relativeDirPath);
    if (hit) {
      return hit;
    }
    let dir = root;
    let path = '';
    for (const part of relativeDirPath.split('/').filter(Boolean)) {
      path = path ? `${path}/${part}` : part;
      const cached = cache.get(path);
      if (cached) {
        dir = cached;
        continue;
      }
      dir = await dir.getDirectoryHandle(this.sanitizeFsName(part), { create: true });
      cache.set(path, dir);
    }
    return dir;
  }

  /** Approach C: real folder structure, silent after one-time setup. */
  private downloadFolderTreeToDirectory(
    manifestUrl: string,
    buildFileUrl: (fileId: number) => string
  ): Observable<HttpEvent<Blob>> {
    return new Observable<HttpEvent<Blob>>((subscriber) => {
      let cancelled = false;
      this.downloadActivity.begin();
      (async () => {
        // Under the user gesture: resolve the persisted destination (or the
        // one-time picker on first ever use).
        const baseDir = await ensureDownloadDirectory();

        const manifest = await firstValueFrom(this.folderManifest$(manifestUrl));
        const files = manifest?.files ?? [];
        // relativePath already starts with the shared folder's name (BuildFolderPaths
        // includes the root), so write directly under the picked destination —
        // creating another root here would duplicate the parent folder.
        const root = baseDir;
        const topLevelName = this.sanitizeFsName(manifest?.folderName || 'Folder');

        const total = files.reduce((sum, f) => sum + (f.size || 0), 0);
        let loaded = 0;
        const failures: string[] = [];
        const dirCache = new Map<string, any>();

        const emitProgress = () =>
          subscriber.next({
            type: HttpEventType.DownloadProgress,
            loaded,
            total: total > 0 ? total : undefined,
          } as HttpEvent<Blob>);
        emitProgress();

        // Files stream strictly one at a time (chunked Range requests inside).
        for (const entry of files) {
          if (cancelled) {
            return;
          }
          try {
            const parts = (entry.relativePath || entry.fileName || `File_${entry.fileId}`)
              .split('/')
              .filter(Boolean);
            const name = this.sanitizeFsName(parts.pop() || `File_${entry.fileId}`);
            const dir = await this.ensureDirectory(root, parts.join('/'), dirCache);
            const fileHandle = await dir.getFileHandle(name, { create: true });
            const writable = await fileHandle.createWritable();

            let prevLoaded = 0;
            await new Promise<void>((resolve, reject) => {
              this.downloadUrlInChunks(buildFileUrl(entry.fileId), {
                knownTotalBytes: entry.size > 0 ? entry.size : undefined,
                fileName: name,
                writable,
              }).subscribe({
                next: (e) => {
                  if (e.type === HttpEventType.DownloadProgress) {
                    const l = (e as any).loaded ?? 0;
                    loaded += l - prevLoaded;
                    prevLoaded = l;
                    emitProgress();
                  }
                },
                error: reject,
                complete: resolve,
              });
            });
          } catch (err: any) {
            failures.push(
              `${entry.relativePath || entry.fileName}: ${err?.message ?? String(err)}`
            );
          }
        }

        if (cancelled) {
          return;
        }

        if (failures.length > 0) {
          try {
            // Put the report inside the downloaded folder, not the destination root.
            const reportDir = await this.ensureDirectory(root, topLevelName, dirCache);
            const report = await reportDir.getFileHandle('_FAILED_FILES.txt', { create: true });
            const w = await report.createWritable();
            await w.write(
              'The following files could not be downloaded:\r\n\r\n' + failures.join('\r\n')
            );
            await w.close();
          } catch {
            /* best effort */
          }
          console.warn('[ShareTransfer] folder download finished with failures', failures);
        }

        subscriber.next(new HttpResponse<Blob>({ body: createDiskSavedBlob(), status: 200 }));
        subscriber.complete();
      })()
        .catch((err) => {
          if (!cancelled) {
            subscriber.error(
              err?.name === 'AbortError' ? new Error('Download cancelled.') : err
            );
          }
        })
        .finally(() => this.downloadActivity.end());
      return () => {
        cancelled = true;
      };
    });
  }

  /** Polls prepare-download status with exponential backoff (2s→4s→8s, cap 15s). */
  private pollPrepareDownloadStatus(
    statusUrl: string,
    initialDelayMs = 2000,
    maxDelayMs = 15000,
    timeoutMs = 30 * 60 * 1000
  ): Observable<{ fileName: string; size: number; warning?: string | null }> {
    let attempt = 0;
    const poll = (): Observable<{ fileName: string; size: number; warning?: string | null }> =>
      this.http
        .get<{ status: string; fileName?: string; size?: number; error?: string; warning?: string | null }>(
          statusUrl,
          { headers: { 'X-Skip-Loader': 'true' } }
        )
        .pipe(
          switchMap((sr) => {
            if (sr.status === 'ready') {
              return of({ fileName: sr.fileName || '', size: sr.size || 0, warning: sr.warning });
            }
            if (sr.status === 'failed') {
              return throwError(() => new Error(sr.error || 'Failed to prepare download.'));
            }
            const delay = Math.min(initialDelayMs * Math.pow(2, attempt++), maxDelayMs);
            return timer(delay).pipe(switchMap(() => poll()));
          })
        );
    return poll().pipe(timeout(timeoutMs));
  }

  /** Non-Chromium fallback: GCS-prepared zip, ≤ 1 GB only. */
  private downloadFolderViaPreparedZip(
    manifestUrl: string,
    zip: {
      prepareUrl: string;
      statusUrl: (downloadId: string) => string;
      preparedUrl: (downloadId: string) => string;
      suggestedName: string;
    }
  ): Observable<HttpEvent<Blob>> {
    return defer(() => {
      this.downloadActivity.begin();
      return this.folderManifest$(manifestUrl).pipe(
        switchMap((manifest) => {
          const total = (manifest?.files ?? []).reduce((sum, f) => sum + (f.size || 0), 0);
          if (total > DocumentService.FALLBACK_ZIP_MAX_BYTES) {
            const gb = (total / (1024 * 1024 * 1024)).toFixed(1);
            return throwError(
              () =>
                new Error(
                  `This folder is ${gb} GB. Folder downloads over 1 GB are not supported in this browser — please use Chrome or Edge.`
                )
            );
          }
          return this.http.post<{ downloadId: string }>(
            zip.prepareUrl,
            {},
            { headers: { 'X-Skip-Loader': 'true' } }
          );
        }),
        switchMap((prep) =>
          this.pollPrepareDownloadStatus(zip.statusUrl(prep.downloadId)).pipe(
            map((ready) => ({ ...ready, downloadId: prep.downloadId }))
          )
        ),
        switchMap((ready) => {
          if (ready.warning) {
            console.warn('[ShareTransfer] prepared zip warning:', ready.warning);
          }
          return this.downloadUrlInChunks(zip.preparedUrl(ready.downloadId), {
            knownTotalBytes: ready.size,
            fileName: ready.fileName || zip.suggestedName,
            streamToDisk: false, // memory blob ≤ 1 GB; component saves it
          });
        }),
        finalize(() => this.downloadActivity.end())
      );
    });
  }

  downloadFolder(folderId: any): Observable<HttpResponse<Blob>> {
    return this.downloadFolderWithProgress(folderId).pipe(
      filter((e): e is HttpResponse<Blob> => e.type === HttpEventType.Response)
    );
  }

  /** Direct-to-disk folder download via native browser downloads (no zip). */
  downloadFolderWithProgress(
    folderId: any,
    options?: { fileName?: string; streamToDisk?: boolean }
  ): Observable<HttpEvent<Blob>> {
    const manifestUrl = this.apiUrl + ApiUrls.folderManifest + folderId;
    return this.downloadFolderTreeToDisk(
      manifestUrl,
      (fileId) => this.apiUrl + ApiUrls.getFileFromCloud + fileId,
      {
        prepareUrl: this.apiUrl + ApiUrls.prepareFolderDownload + folderId,
        statusUrl: (id) => this.apiUrl + ApiUrls.folderDownloadStatus + encodeURIComponent(id),
        preparedUrl: (id) =>
          this.apiUrl + ApiUrls.downloadPreparedFolder + encodeURIComponent(id),
        suggestedName: options?.fileName || `Folder_${folderId}.zip`,
      }
    );
  }

  // Favourite
  getMyFavourites(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getMyFavourites;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  addFavourite(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.addFavourite;
    return this.http.post<any>(link, payload);
  }

  removeFavourite(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.removeFavourite;
    return this.http.post<any>(link, payload);
  }

  // Recycle Bin
  getRecycleBinItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getRecycleBinItems;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  getArchivedItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getArchivedItems;
    return this.http.post<any>(link, payload, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  deleteForever(payload: any): Observable<any> {
    // Check if payload has FileIds or FolderIds (multiple items) or single item
    if (payload.FileIds || payload.FolderIds) {
      // Multiple items - send in request body
      const link = this.apiUrl + ApiUrls.deleteForever;
      return this.http.post<any>(link, payload);
    } else {
      // Single item - use query parameters (backward compatible)
      const link = this.apiUrl + ApiUrls.deleteForever + `?itemType=${payload.itemType}&entityId=${payload.entityId}`;
      return this.http.post<any>(link, null);
    }
  }

  deleteUserGuide(id: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.deleteUserGuide + `/${id}`;
    return this.http.post<any>(link, null);
  }

  activateDeactivateUserGuide(id: number, action: string): Observable<any> {
    const link = this.apiUrl + ApiUrls.activateDeactivateUserGuide+ `/${id}`;
    return this.http.post<any>(link, null);
  }

  getActiveUserGuide(): Observable<any> {
    const link = this.apiUrl + ApiUrls.getActiveUserGuide;
    return this.http.get<any>(link);
  }

  getAllExternalUserAPI(): Observable<any> {
    const link = this.apiUrl + ApiUrls.getAllExternalUserAPI;
    return this.http.get<any>(link);
  }

  fileRestore(type: any, itemId: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.fileRestore + `?itemType=${type}&entityId=${itemId}`;
    return this.http.post<any>(link, {});
  }

  unarchiveItem(type: any, itemId: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.unarchiveItem + `?itemType=${type}&entityId=${itemId}`;
    return this.http.post<any>(link, {});
  }

  getSearchItems(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSearchItems;
    return this.http.post<any>(link, payload);
  }

  getSearchItemsClassic(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getSearchItemsClassic;
    return this.http.post<any>(link, payload);
  }

  globalSearch(payload: GlobalSearchPayload): Observable<any> {
    const link = this.apiUrl + ApiUrls.globalSearch;
    return this.http.post<any>(link, payload);
  }

  validateShareToken(token: string): Observable<any> {
    const link = this.apiUrl + ApiUrls.validateShareToken + `?token=${token}`;
    return this.http.get<any>(link);
  }

  processShareLink(token: string, fileId?: number, folderId?: number): Observable<any> {
    let link = this.apiUrl + ApiUrls.processShareLink + `?token=${token}`;
    if (fileId) {
      link += `&fileId=${fileId}`;
    }
    if (folderId) {
      link += `&folderId=${folderId}`;
    }
    return this.http.post<any>(link, {});
  }

  resendInvitationEmail(payload: any): Observable<any> {
    const link = this.apiUrl + BackofficeApiUrls.resendInvitationEmail;
    return this.http.post<any>(link, payload);
  }

  resetPasswordForUser(payload: any): Observable<any> {
    const link = this.apiUrl + BackofficeApiUrls.changePassword;
    return this.http.post<any>(link, payload);
  }

  getAllLocations(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.getAllLocations;
    return this.http.post<any>(link, payload);
  }

  createLocation(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.createLocation;
    return this.http.post<any>(link, payload);
  }

  updateLocation(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateLocation;
    return this.http.post<any>(link, payload);
  }

  deleteLocation(payload: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.deleteLocation + `?id=${payload.id}`;
    return this.http.post<any>(link, payload);
  }

  getLocationById(id: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.getLocationById + `/${id}`;
    return this.http.get<any>(link);
  }

  
  createTag(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.createTag;
    return this.http.post<any>(link, body);
  }

  updateTag(body: any): Observable<any> {
    const link = this.apiUrl + ApiUrls.updateTag + '/' + body.tagId;
    return this.http.post<any>(link, body);
  }

  deleteTag(tagId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.deleteTag + '/' + tagId;
    return this.http.post<any>(link, null);
  }

  activeTag(tagId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.activeTag + '/' + tagId;
    return this.http.post<any>(link, null);
  }

  deActivateTag(tagId: number): Observable<any> {
    const link = this.apiUrl + ApiUrls.deActivateTag + '/' + tagId;
    return this.http.post<any>(link, null);
  }

  private attachEventCalendarDimensions(response: any): any {
    const items = response?.data?.items || response?.data || [];
    if (!Array.isArray(items)) {
      return response;
    }

    const dimensions = this.extractEventCalendarDimensions(items);
    if (response?.data && typeof response.data === 'object') {
      return {
        ...response,
        data: {
          ...response.data,
          calendarDimensions: dimensions,
        },
      };
    }

    return {
      ...response,
      calendarDimensions: dimensions,
    };
  }

  private extractEventCalendarDimensions(items: any[]): {
    years: number[];
    yearMonths: { year: number; month: number }[];
  } {
    const yearSet = new Set<number>();
    const yearMonthSet = new Set<string>();

    for (const event of items) {
      const dateCandidates = [
        event?.eventDate,
        event?.eventStartDate,
        event?.eventEndDate,
        event?.startDate,
        event?.endDate];

      for (const rawDate of dateCandidates) {
        const parsed = this.safeParseDate(rawDate);
        if (!parsed) {
          continue;
        }
        const year = parsed.getFullYear();
        const month = parsed.getMonth() + 1;
        yearSet.add(year);
        yearMonthSet.add(`${year}-${month}`);
      }
    }

    const years = Array.from(yearSet).sort((a, b) => a - b);
    const yearMonths = Array.from(yearMonthSet)
      .map((value) => {
        const [year, month] = value.split('-').map((x) => Number(x));
        return { year, month };
      })
      .sort((a, b) => (a.year - b.year) || (a.month - b.month));

    return { years, yearMonths };
  }

  private safeParseDate(value: any): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
